import React, { createContext, useContext, useState, useEffect } from "react";

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Get language from localStorage or default to Japanese
    return localStorage.getItem("language") || "ja";
  });

  useEffect(() => {
    // Save language preference to localStorage
    localStorage.setItem("language", language);
  }, [language]);

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === "en" ? "ja" : "en"));
  };

  const setLanguageAndSync = (lang) => {
    setLanguage(lang);
  };

  const t = (key) => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider
      value={{ language, setLanguage: setLanguageAndSync, toggleLanguage, t }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

// Translation dictionary
const translations = {
  en: {
    // Navigation
    "nav.dashboard": "Dashboard",
    "nav.settings": "Settings",
    "nav.users": "Users",
    "nav.adminSettings": "Rate Limit Settings",
    "nav.logout": "Logout",
    "nav.appTitle": "Cube Support Desk",

    // Dashboard
    "dashboard.title": "Dashboard",
    "dashboard.welcome": "Welcome, {name}!",
    "dashboard.role": "Role",
    "dashboard.email": "Email",
    "dashboard.wallet": "Wallet",
    "dashboard.myTickets": "Tickets",
    "dashboard.open": "Open",
    "dashboard.inProgress": "In Progress",
    "dashboard.replyReceived": "Reply Received",
    "dashboard.closed": "Closed",
    "dashboard.supportTickets": "My Support Tickets",
    "dashboard.createTicket": "Create New Ticket",
    "dashboard.noTickets": "No tickets found. Create your first ticket!",

    // Ticket Table
    "ticket.number": "#",
    "ticket.title": "Title",
    "ticket.createdBy": "Created By",
    "ticket.status": "Status",
    "ticket.priority": "Priority",
    "ticket.category": "Category",
    "ticket.createdAt": "Created",

    // Ticket Status
    "status.open": "Open",
    "status.inProgress": "In Progress",
    "status.waitingReply": "Waiting Reply",
    "status.resolved": "Resolved",
    "status.closed": "Closed",

    // Ticket Priority
    "priority.low": "Low",
    "priority.medium": "Medium",
    "priority.high": "High",
    "priority.urgent": "Urgent",

    // Ticket Category
    "category.technical": "Technical",
    "category.billing": "Billing",
    "category.general": "General",
    "category.featureRequest": "Feature Request",
    "category.other": "Other",

    // Ticket Detail
    "ticketDetail.backToDashboard": "Back to Dashboard",
    "ticketDetail.ticketId": "Ticket ID",
    "ticketDetail.createdBy": "Created by",
    "ticketDetail.createdOn": "Created on",
    "ticketDetail.closeTicket": "Close Ticket",
    "ticketDetail.closeConfirm": "Are you sure you want to close this ticket?",
    "ticketDetail.closeSuccess": "Ticket closed successfully",
    "ticketDetail.closeFailed": "Failed to close ticket",
    "ticketDetail.closedMessage":
      "This ticket is closed and cannot accept new comments.",
    "ticketDetail.comments": "Comments",
    "ticketDetail.addComment": "Add a Comment",
    "ticketDetail.commentPlaceholder": "Type your comment here...",
    "ticketDetail.postComment": "Post Comment",
    "ticketDetail.posting": "Posting...",
    "ticketDetail.system": "System",
    "ticketDetail.systemTag": "System",
    "ticketDetail.notFound": "Ticket not found",
    "ticketDetail.internalNotes": "Internal Notes (Staff Only)",
    "ticketDetail.internalNotesDesc":
      "This section is only visible to support staff and administrators. Users cannot see this information.",
    "ticketDetail.internalNotesPlaceholder":
      "Add internal notes about this ticket...",
    "ticketDetail.saveNotes": "Save Notes",
    "ticketDetail.saving": "Saving...",
    "ticketDetail.notesSaved": "Internal notes saved successfully",
    "ticketDetail.notesSaveFailed": "Failed to save internal notes",

    // Create Ticket Dialog
    "createTicket.title": "Create New Ticket",
    "createTicket.ticketTitle": "Title",
    "createTicket.description": "Description",
    "createTicket.priority": "Priority",
    "createTicket.category": "Category",
    "createTicket.cancel": "Cancel",
    "createTicket.create": "Create Ticket",
    "createTicket.creating": "Creating...",
    "createTicket.rateLimitError":
      "You have reached the maximum limit of 3 tickets per 24 hours. Please try again later.",

    // Close Dialog
    "closeDialog.title": "Close Ticket",
    "closeDialog.message": "Are you sure you want to close this ticket?",
    "closeDialog.cancel": "Cancel",
    "closeDialog.confirm": "Yes, Close",

    // System Messages
    "system.ticketClosed":
      "This ticket has been closed.\n\nIf you have additional questions, please create a new ticket.",

    // Common
    "common.yes": "Yes",
    "common.no": "No",
    "common.cancel": "Cancel",
    "common.save": "Save",
    "common.delete": "Delete",
    "common.edit": "Edit",
    "common.loading": "Loading...",
    "common.error": "Error",
    "common.success": "Success",

    // Login
    "login.title": "Login to Support Desk",
    "login.withGoogle": "Login with Google",
    "login.withMetamask": "Login with Metamask",
    "login.accountNotice":
      "Please log in with the same account you use for Cube-Casino.",

    // Settings
    "settings.title": "Support Settings",
    "settings.subtitle": "Display name and email notification settings",
    "settings.accountInfo": "Account Information",
    "settings.email": "Email Address",
    "settings.role": "Role",
    "settings.roleAdmin": "Administrator",
    "settings.roleSupport": "Support Staff",
    "settings.roleUser": "User",
    "settings.accessDenied": "This feature is only available to support staff",
    "settings.displayName": "Display Name",
    "settings.displayNameHelper":
      "This name will be displayed when you comment on tickets",
    "settings.notificationEmail": "Notification Email",
    "settings.notificationEmailHelper":
      "You will receive email notifications for new tickets at this address",
    "settings.saveSuccess": "Settings saved successfully",
    "settings.title": "Rate Limit Settings",
    "settings.description": "Configure ticket creation rate limits for users",
    "settings.maxTicketsLabel": "Maximum Tickets",
    "settings.maxTicketsHelp":
      "Maximum number of tickets a user can create per time window (0 = unlimited)",
    "settings.timeWindowLabel": "Time Window (hours)",
    "settings.timeWindowHelp": "Time period in hours for rate limiting",
    "settings.reset": "Reset",
    "settings.save": "Save Settings",
    "settings.loadError": "Failed to load settings",
    "settings.saveError": "Failed to save settings",
    "settings.invalidMaxTickets": "Maximum tickets must be 0 or greater",
    "settings.invalidTimeWindow": "Time window must be at least 0.1 hours",
  },
  ja: {
    // Navigation
    "nav.dashboard": "ダッシュボード",
    "nav.settings": "設定",
    "nav.users": "ユーザー管理",
    "nav.logout": "ログアウト",
    "nav.appTitle": "Cube サポートデスク",

    // Dashboard
    "dashboard.title": "ダッシュボード",
    "dashboard.welcome": "ようこそ、{name}さん！",
    "dashboard.role": "ロール",
    "dashboard.email": "メール",
    "dashboard.wallet": "ウォレット",
    "dashboard.myTickets": "チケット",
    "dashboard.open": "オープン",
    "dashboard.inProgress": "対応中",
    "dashboard.replyReceived": "返答あり",
    "dashboard.closed": "クローズ",
    "dashboard.supportTickets": "サポートチケット一覧",
    "dashboard.createTicket": "新規チケット作成",
    "dashboard.noTickets":
      "チケットが見つかりません。最初のチケットを作成してください！",

    // Ticket Table
    "ticket.number": "番号",
    "ticket.title": "タイトル",
    "ticket.createdBy": "作成者",
    "ticket.status": "状態",
    "ticket.priority": "優先度",
    "ticket.category": "種別",
    "ticket.createdAt": "作成日",

    // Ticket Status
    "status.open": "オープン",
    "status.inProgress": "対応中",
    "status.waitingReply": "返信待ち",
    "status.resolved": "解決済み",
    "status.closed": "クローズ",

    // Ticket Priority
    "priority.low": "低",
    "priority.medium": "中",
    "priority.high": "高",
    "priority.urgent": "緊急",

    // Ticket Category
    "category.technical": "技術的問題",
    "category.billing": "請求",
    "category.general": "一般",
    "category.featureRequest": "機能リクエスト",
    "category.other": "その他",

    // Ticket Detail
    "ticketDetail.backToDashboard": "ダッシュボードに戻る",
    "ticketDetail.ticketId": "チケットID",
    "ticketDetail.createdBy": "作成者",
    "ticketDetail.createdOn": "作成日",
    "ticketDetail.closeTicket": "チケットをクローズ",
    "ticketDetail.closeConfirm": "本当にこのチケットをクローズしますか？",
    "ticketDetail.closeSuccess": "チケットをクローズしました",
    "ticketDetail.closeFailed": "チケットのクローズに失敗しました",
    "ticketDetail.closedMessage":
      "このチケットはクローズされているため、コメントを投稿できません。",
    "ticketDetail.comments": "コメント",
    "ticketDetail.addComment": "コメントを追加",
    "ticketDetail.commentPlaceholder": "ここにコメントを入力してください...",
    "ticketDetail.postComment": "コメントを投稿",
    "ticketDetail.posting": "投稿中...",
    "ticketDetail.system": "システム",
    "ticketDetail.systemTag": "システム",
    "ticketDetail.notFound": "チケットが見つかりません",
    "ticketDetail.internalNotes": "内部メモ（スタッフ専用）",
    "ticketDetail.internalNotesDesc":
      "このセクションはサポートスタッフと管理者のみに表示されます。ユーザーには見えません。",
    "ticketDetail.internalNotesPlaceholder":
      "このチケットに関する内部メモを追加...",
    "ticketDetail.saveNotes": "メモを保存",
    "ticketDetail.saving": "保存中...",
    "ticketDetail.notesSaved": "内部メモを保存しました",
    "ticketDetail.notesSaveFailed": "内部メモの保存に失敗しました",

    // Create Ticket Dialog
    "createTicket.title": "新規チケット作成",
    "createTicket.ticketTitle": "タイトル",
    "createTicket.description": "説明",
    "createTicket.priority": "優先度",
    "createTicket.category": "カテゴリ",
    "createTicket.cancel": "キャンセル",
    "createTicket.create": "チケットを作成",
    "createTicket.creating": "作成中...",
    "createTicket.rateLimitError":
      "24時間以内に作成できるチケットは3つまでです。しばらくしてから再度お試しください。",

    // Close Dialog
    "closeDialog.title": "チケットをクローズ",
    "closeDialog.message": "本当にこのチケットをクローズしますか？",
    "closeDialog.cancel": "キャンセル",
    "closeDialog.confirm": "はい、クローズします",

    // System Messages
    "system.ticketClosed":
      "このチケットはクローズされました。\n\n追加で質問がある場合は、新しいチケットを作成してください。",

    // Common
    "common.yes": "はい",
    "common.no": "いいえ",
    "common.cancel": "キャンセル",
    "common.save": "保存",
    "common.delete": "削除",
    "common.edit": "編集",
    "common.loading": "読み込み中...",
    "common.error": "エラー",
    "common.success": "成功",

    // Login
    "login.title": "サポートデスクにログイン",
    "login.withGoogle": "Googleでログイン",
    "login.withMetamask": "Metamaskでログイン",
    "login.accountNotice":
      "Cube-Casinoのログインアカウントと同じアカウントでログインしてください。",

    // Settings
    "settings.title": "サポート設定",
    "settings.subtitle": "問い合わせ対応時の表示名とメール通知設定",
    "settings.accountInfo": "アカウント情報",
    "settings.email": "メールアドレス",
    "settings.role": "ロール",
    "settings.roleAdmin": "管理者",
    "settings.roleSupport": "担当者",
    "settings.roleUser": "一般ユーザー",
    "settings.accessDenied": "この機能は担当者のみ利用できます",
    "settings.displayName": "表示名",
    "settings.displayNameHelper":
      "チケットにコメントする際にこの名前が表示されます",
    "settings.notificationEmail": "通知用メールアドレス",
    "settings.notificationEmailHelper":
      "このアドレスに新しいチケットの通知が届きます",
    "settings.saveSuccess": "設定を保存しました",
    "settings.title": "制限設定",
    "settings.description": "ユーザーのチケット作成の制限を設定します",
    "settings.maxTicketsLabel": "最大チケット数",
    "settings.maxTicketsHelp":
      "指定期間内にユーザーが作成できるチケットの最大数（0 = 無制限）",
    "settings.timeWindowLabel": "期間（時間）",
    "settings.timeWindowHelp": "制限を適用する期間（時間単位）",
    "settings.reset": "リセット",
    "settings.save": "設定を保存",
    "settings.loadError": "設定の読み込みに失敗しました",
    "settings.saveError": "設定の保存に失敗しました",
    "settings.invalidMaxTickets": "最大チケット数は0以上である必要があります",
    "settings.invalidTimeWindow": "期間は最低0.1時間である必要があります",
    "nav.adminSettings": "制限設定",
  },
};
